/**
 * Router setup: assembles the area layouts with the route arrays from
 * route_configs.ts and installs the navigation guards/callbacks (real
 * session guard, document title, server version check).
 */
import {trpcClient} from '@shared_ui/trpc_client';
import {__APP_ENV__} from '@src/app_env';
import {useAuthStore} from '@src/store/auth_store';
import type {Router, RouteRecordRaw} from 'vue-router';
import {createRouter, createWebHistory} from 'vue-router';
import {
   ADMIN_ROUTES,
   AUTH_ROUTES,
   BASE_ROUTES,
   DEV_ROUTES,
   MAIN_ROUTES,
   SETTINGS_ROUTES,
} from './route_configs';
import {RouteNames} from './route_names';

const MainLayout = () => import('@c/layout/MainLayout.vue');
const AuthLayout = () => import('@c/auth/AuthLayout.vue');
const SettingsLayout = () => import('@c/settings/SettingsLayout.vue');
const AdminLayout = () => import('@c/admin/AdminLayout.vue');
const DevLayout = () => import('@c/dev/DevLayout.vue');

/** localStorage key used to avoid version-reload loops. */
const VERSION_RELOAD_KEY = 'ab-version-reloaded-for';

/** Build the route table: base routes plus one parent route per area layout. */
function getRoutes(): RouteRecordRaw[] {
   return [
      ...BASE_ROUTES,
      {
         path: '/auth',
         component: AuthLayout,
         children: AUTH_ROUTES,
      },
      {
         path: '/boards',
         component: MainLayout,
         children: MAIN_ROUTES,
      },
      {
         path: '/settings',
         component: SettingsLayout,
         meta: {requiresAuth: true},
         children: SETTINGS_ROUTES,
      },
      {
         path: '/admin',
         component: AdminLayout,
         meta: {requiresAuth: true, requiresAdmin: true},
         children: ADMIN_ROUTES,
      },
      {
         path: '/dev',
         component: DevLayout,
         // App admins only, in every environment (masked as NotFound)
         meta: {requiresAuth: true, requiresAdmin: true},
         children: DEV_ROUTES,
      },
   ];
}

/**
 * Create and configure the application router.
 */
export function setupRoutes(): Router {
   const router = createRouter({
      history: createWebHistory(),
      scrollBehavior: () => ({top: 0}),
      linkActiveClass: 'active-link',
      routes: getRoutes(),
   });

   router.beforeEach(async (to) => {
      // Protected routes wait for the server session check before rendering.
      // Organization-role gating via `to.meta.allowedRoles` is not enforced yet.
      const auth_store = useAuthStore();

      // If we don't need auth, still sync the session so any UI chrome can use it
      if (to.meta.requiresAuth !== true) {
         auth_store.syncSessionInBackground();
         return true;
      }

      await auth_store.validateSession();

      // Non-admins, signed in or not, get the not-found page at the requested
      // URL so admin routes are never revealed.
      if (to.meta.requiresAdmin === true && !auth_store.isSystemAdmin) {
         return {
            name: RouteNames.NOT_FOUND,
            params: {pathMatch: to.path.substring(1).split('/')},
            query: to.query,
            hash: to.hash,
         };
      }

      if (!auth_store.isAuthenticated) {
         return {name: RouteNames.AUTH_SIGN_IN, query: {redirect: to.fullPath}};
      }
      return true;
   });

   router.afterEach((to) => {
      // Keep the document title in sync with the route
      const title = to.meta.pageTitle ?? to.meta.menuName ?? null;
      document.title = title == null ? 'Amazing Boards' : `${title} – Amazing Boards`;

      // Check for a new server version in the background on navigation
      void triggerUpdateCheck();
   });

   return router;
}

/**
 * Reload the page when the server reports a version the client does not
 * match, at most once per server version. Failures are logged and ignored.
 */
async function triggerUpdateCheck(): Promise<void> {
   try {
      const client_version = __APP_ENV__.PACKAGE_VERSION as string;

      const version_resp = await trpcClient().health.version.query();
      const server_version = version_resp.version;
      const last_server_version_reloaded = localStorage.getItem(VERSION_RELOAD_KEY) ?? 'empty';

      const out_of_date = !client_version.startsWith(server_version);
      if (out_of_date) {
         console.warn(
            `Client and server versions do not match: Server: ${server_version}, Client: ${client_version}`,
         );
      }

      // localStorage remembers the version reloaded for, so a mismatch cannot loop
      if (out_of_date && last_server_version_reloaded !== server_version) {
         console.log(`Reloading for new server version: ${server_version}`);
         localStorage.setItem(VERSION_RELOAD_KEY, server_version);
         globalThis.location.reload();
      }
   } catch (e) {
      console.warn('Failed to check for new version:', e);
   }
}
